import React from "react"
import { useTranslations } from "next-intl"

import { generateBasePriceInfo } from "@/lib/price-utils"
import { BasketLineItemData } from "@/lib/storefront/basket/basket"
import { cn } from "@/components/ui/helpers/utils"

export function BasketLineItemPriceInfo(props: {
  isB2B: boolean
  isoLocale: string
  lineItem: BasketLineItemData | undefined
  showAllPrices?: boolean
}) {
  const t = useTranslations("storefront")
  const lineItem = props.lineItem

  const discountType = lineItem?.discountType ?? ""
  const isDiscounted = lineItem?.isDiscounted

  const pricePerUnitText = generateBasePriceInfo(
    props.isoLocale,
    props?.lineItem?.product,
    props.isB2B ? props?.lineItem?.singleSalePriceNet : props?.lineItem?.singleSalePriceGross
  )

  return (
    <div
      className={cn("flex h-full flex-col justify-between gap-1", {
        "justify-end": !props.isB2B,
      })}
    >
      {/*  <pre>{JSON.stringify(lineItem)}</pre>*/}

      {(props.isB2B || props.showAllPrices) && (
        <div className={"flex flex-col justify-start gap-1"}>
          <div className={"flex flex-col items-end justify-start"}>
            <div
              className={cn("hidden flex-row justify-end gap-1 text-xs", {
                flex: isDiscounted,
              })}
            >
              <p className={"min-w-24 text-right font-bold"}>{t("basket.add_dialog.normal")}:</p>
              <p
                className={cn("min-w-24 text-right decoration-primary", {
                  "line-through": isDiscounted,
                })}
              >
                {lineItem?.totalListPriceFormatted}
              </p>
            </div>
            <div
              className={cn("hidden flex-row justify-end gap-1 text-xs", {
                flex: isDiscounted,
              })}
            >
              {(() => {
                switch (discountType) {
                  /*Preisliste*/

                  case "PRICELIST":
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t("basket.add_dialog.discount", {
                          discountPercentFormatted: lineItem?.discountPercentFormatted,
                        })}
                        :
                      </p>
                    )
                  /*Rahmenrabatt*/
                  case "GENERAL":
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t("basket.add_dialog.discount_general", {
                          discountPercentFormatted: lineItem?.discountPercentFormatted,
                        })}
                        :
                      </p>
                    )
                  /*Gutschein*/
                  case "VOUCHER":
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t("basket.add_dialog.discount_voucher", {
                          discountPercentFormatted: lineItem?.discountPercentFormatted,
                        })}
                        :
                      </p>
                    )
                  /*Sonderrabatt Kategorie*/
                  case "CATEGORY":
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t("basket.add_dialog.discount_category", {
                          discountPercentFormatted: lineItem?.discountPercentFormatted,
                        })}
                        :
                      </p>
                    )
                  /*Sonderrabatt Kategorie*/
                  default:
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t("basket.add_dialog.discount_category", {
                          discountPercentFormatted: lineItem?.discountPercentFormatted,
                        })}
                        :
                      </p>
                    )
                }
              })()}

              <p className={"min-w-24 text-right"}>{lineItem?.discountAmountFormatted}</p>
            </div>

            <div
              className={cn("hidden flex-row justify-end gap-1 text-xs", {
                flex: (lineItem?.ecoTax ?? 0) > 0 && lineItem?.ecoTaxFormatted && lineItem.ecoTaxFormatted !== "",
              })}
            >
              <p className={"min-w-24 text-right"}>{t("basket.add_dialog.eco_tax")}:</p>
              <p className={"min-w-24 text-right"}>{lineItem?.ecoTaxFormatted}</p>
            </div>
            <div className={"flex flex-row justify-between gap-1 text-xs"}>
              <p className={"min-w-24 text-right"}>{t("basket.add_dialog.single")}:</p>
              <p className={"min-w-24 text-right"}>{lineItem?.salePriceFormatted}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        {!props.isB2B && isDiscounted && (
          <div className={"flex flex-row justify-end gap-1"}>
            {/*Aktionspreis*/}
            {discountType === "VOUCHER" ? (
              <p className={"text-nowrap text-xs font-bold text-primary"}>{t("product.special_offer_voucher")}</p>
            ) : (
              <p className={"text-nowrap text-xs font-bold text-primary"}>{t("product.special_offer")}</p>
            )}
          </div>
        )}
        {lineItem?.originalProduct?.isDoNotPromoteAll && (
          <div className={"flex flex-row justify-end gap-1 text-xs"}>
            <p className={"text-xs font-bold text-primary"}>{t("product.do_not_promote_all")}</p>
          </div>
        )}
        <div className={"flex flex-row items-center justify-end gap-1 text-sm"}>
          <p className={"min-w-24 text-right"}>{t("basket.add_dialog.sum")}:</p>
          <p className={"min-w-24 text-right text-lg font-bold text-primary"}>{lineItem?.totalSalePriceFormatted}</p>
        </div>
        {lineItem?.product.packagingUnitInfo && lineItem?.product.packagingUnitInfo !== "" && (
          <div className={"flex flex-row items-center justify-end gap-1 text-sm"}>
            <p className={"w-full min-w-24 text-right text-xs"}>{lineItem?.product.packagingUnitInfo ?? ""}</p>
          </div>
        )}

        {pricePerUnitText && pricePerUnitText !== "" && (
          <div className={"flex flex-row items-center justify-end gap-1 text-sm"}>
            <p className={"w-full min-w-24 text-right text-xs"}>{pricePerUnitText ?? ""}</p>
          </div>
        )}
      </div>
    </div>
  )
}
